import { db } from '@/firebase/init';
import { connectorCardReducer } from '@/reducers/connectorCardReducer';
import { ConnectorStatus } from '@/types/Connectors';
import { ConnectorCardReducerState } from '@/types/reducers';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useReducer, useState } from 'react';

export function parseStatusToReducerState(
  status: ConnectorStatus,
  isInitialSetup: boolean,
  isSyncing: boolean,
): ConnectorCardReducerState {
  if (isInitialSetup) {
    return { status: 'Under_Setup', isLoading: true, isActive: true };
  }
  switch (status) {
    case 'ACTIVATION_SUCCESS':
      if (isSyncing) return { status: 'Syncing', isLoading: false, isActive: true };
      return { status: 'Success', isLoading: false, isActive: true };
    case 'ACTIVATION_FAILURE':
      return {
        status: 'Failure',
        errorType: 'ACTIVATION_FAILURE',
        isLoading: false,
        isActive: false,
      };
    case 'DEACTIVATION_SUCCESS':
      return { status: 'Success', isLoading: false, isActive: false };
    case 'DEACTIVATION_FAILURE':
      return {
        status: 'Failure',
        errorType: 'DEACTIVATION_FAILURE',
        isLoading: false,
        isActive: true,
      };
    case 'ACTIVATION_AWAITING':
      return { status: 'Activating', isLoading: true, isActive: false };
    case 'DEACTIVATION_AWAITING':
      return { status: 'Deactivating', isLoading: true, isActive: true };
    case 'AWAITING_DELETION':
      return { status: 'Deleting', isLoading: true, isActive: false };
    default:
      return { status: 'Init', isLoading: false, isActive: false };
  }
}

export function useConnectorStateMachine(
  status: ConnectorStatus,
  isInitialSetup: boolean,
  isSyncing: boolean,
  organizationId: string,
  connectorId: string,
) {
  const [prevStatus, setPrevStatus] = useState(status);
  const [prevIsSyncing, setPrevIsSyncing] = useState(isSyncing);
  const [prevIsInitialSetup, setPrevIsInitialSetup] = useState(isInitialSetup);

  const [state, dispatch] = useReducer(
    connectorCardReducer,
    parseStatusToReducerState(status, isInitialSetup, isSyncing),
  );

  if (prevStatus !== status && state.status !== 'Syncing') {
    if (status.includes('FAILURE'))
      dispatch({
        type: 'FAILURE',
        payload: { errorType: status, isActive: !status.includes('DEACTIVATION') },
      });
    // adds !isInitialSetup to avoid changing state when in Under_Setup state
    else if (status.includes('SUCCESS') && !isInitialSetup)
      dispatch({
        type: 'SUCCESS',
        payload: { isActive: !status.includes('DEACTIVATION') },
      });
    else if (status === 'ACTIVATION_AWAITING' && !isInitialSetup)
      dispatch({ type: 'ACTIVATING', payload: { isActive: false } });
    else if (status === 'DEACTIVATION_AWAITING' && !isInitialSetup)
      dispatch({ type: 'DEACTIVATING', payload: { isActive: true } });
    else if (status === 'AWAITING_DELETION')
      dispatch({ type: 'DELETING', payload: { isActive: false } });
    setPrevStatus(status);
  }

  if (prevIsInitialSetup !== isInitialSetup) {
    if (isInitialSetup) {
      dispatch({ type: 'UNDER_SETUP', payload: { isActive: true } });
    } else {
      dispatch({
        type: 'SUCCESS',
        payload: { isActive: !status.includes('DEACTIVATION') },
      });
    }
    setPrevIsInitialSetup(isInitialSetup);
  }

  if (prevIsSyncing !== isSyncing) {
    if (isSyncing) {
      dispatch({ type: 'SYNCING', payload: { isActive: true } });
    } else if (!isSyncing && state.status === 'Syncing') {
      dispatch({
        type: 'SUCCESS',
        payload: { isActive: !status.includes('DEACTIVATION') },
      });
    }
    setPrevIsSyncing(isSyncing);
  }

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, `organizations/${organizationId}/organization_connectors/${connectorId}`),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const newStatus = data.status as ConnectorStatus;
          if (newStatus.includes('FAILURE')) {
            dispatch({
              type: 'FAILURE',
              payload: {
                errorType: newStatus,
                isActive: true,
              },
            });
          }
        }
      },
    );
    return unsub;
  }, [connectorId, organizationId]);

  return [state, dispatch] as const;
}
